import React, { useContext, useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Flex,
  Button,
  Checkbox,
  TextInput,
  Tooltip,
  ActionIcon,
  Group,
  Text,
  ScrollArea,
  Divider,
  Select,
  NumberInput,
} from '@mantine/core';

import {
  IconEdit,
  IconChevronUp,
  IconChevronDown,
  IconArrowBarToRight,
  IconArrowBarToLeft,
  IconInfoCircle,
} from "@tabler/icons";
import SyncContext from '../../context/SyncContext';
import { editSubpage } from '../../Api';
import { useAuth0 } from '@auth0/auth0-react';

export { useEffect } from 'react';

interface EditTableViewModalProps {
  opened: boolean;
  onClose: () => void;
  subPageKey?: string;
  subPage: any;
  setEditMode: (editMode: boolean) => void;
}

export const EditTableViewModal: React.FC<EditTableViewModalProps> = ({ opened, onClose, subPageKey, subPage = {}, setEditMode }) => {
  const { allColumns, ideaColumns } = useContext(SyncContext);
 
  const [viewColumns, setViewColumns] = useState(subPageKey ? subPage?.ideaColumns : ideaColumns)
  // Sort viewColumns by index first
  const sortedViewColumns = [...viewColumns].sort((a: any, b: any) => (a.index || 0) - (b.index || 0));
  const viewColumnAccessors = sortedViewColumns.map((col: any) => col.accessor);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ accessor: string; title: string; width: string; textAlignment: string; renderComponent: string }>({ accessor: '', title: '', width: '', textAlignment: 'left', renderComponent: '' });
  const [search, setSearch] = useState('');
  const auth0 = useAuth0();
  
  const sortedAllColumns = [...allColumns].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  const availableColumns = sortedAllColumns
    .filter((col: any) =>
      (!viewColumnAccessors.includes(col.accessor) || 
       viewColumns.find((vc: any) => vc.accessor === col.accessor)?.enabled === false) &&
      (col.title || '').toLowerCase().includes(search.toLowerCase())
    )
    .map((col: any) => {
      const fullCol = allColumns.find((c: any) => c.accessor === col.accessor) || {};
      return {
        ...col,
        title: col.title ?? fullCol.title ?? '',
        textAlignment: col.textAlignment ?? fullCol.textAlignment ?? 'left',
        renderComponent: col.renderComponent ?? fullCol.renderComponent ?? '',
        width: col.width ?? fullCol.width ?? '',
        index: col.index ?? fullCol.index ?? 0,
      };
    });

  // Move column to right (exclude from view)
  const excludeColumn = (accessor: string) =>
    setViewColumns((cols: any[]) =>
      cols.map((col: any) =>
        col.accessor === accessor ? { ...col, enabled: false } : col
      )
    );

  // Move column to left (include in view at bottom)
  const includeColumn = (accessor: string) => {
    const colObj = sortedAllColumns.find((col: any) => col.accessor === accessor);
    if (!colObj) return;
    let updatedCols: any[] = [];
    setViewColumns((cols: any[]) => {
      const existingCol = cols.find((c: any) => c.accessor === accessor);
      const maxIndex = Math.max(...cols.map((col: any) => col.index));
      if (existingCol) {
        // If column exists but is disabled, enable it
        updatedCols = cols.map((c: any) =>
          c.accessor === accessor ? { ...c, enabled: true, index: maxIndex + 1 } : c
        );
      } else {
        // If column doesn't exist, add it
        // Find the maximum index and add 1
        colObj.index = maxIndex + 1;
        updatedCols = [...cols, colObj];
      }
      return updatedCols.sort((a: any, b: any) => (a.index || 0) - (b.index || 0));
    });
  };

  // Reorder columns (move up/down)
  const moveColumn = (accessor: string, direction: 'up' | 'down') => {
    setViewColumns((cols: any[]) => {
      const tempCols = cols.filter((col: any) => col.enabled === true).sort((a: any, b: any) => (a.index || 0) - (b.index || 0))
      const disabledCols = cols.filter((col: any) => col.enabled === false)
      const idx = tempCols.findIndex((col: any) => col.accessor === accessor);
      if (idx < 0) return cols;
      let newCols = [...tempCols];
      console.log('idx', idx)
      if (direction === 'up' && idx > 0) {
        // Swap index values
        const currentIndex = newCols[idx].index || 0;
        const upperIndex = newCols[idx - 1].index || 0;
        newCols[idx] = { ...newCols[idx], index: upperIndex };
        newCols[idx - 1] = { ...newCols[idx - 1], index: currentIndex };
      }
      if (direction === 'down' && idx < newCols.length - 1) {
        // Swap index values
        const currentIndex = newCols[idx].index || 0;
        const lowerIndex = newCols[idx + 1].index || 0;
        newCols[idx] = { ...newCols[idx], index: lowerIndex };
        newCols[idx + 1] = { ...newCols[idx + 1], index: currentIndex };
      }
      newCols = [...newCols, ...disabledCols]
      return newCols;
    });
  };

  // Edit column (open modal or inline edit)
  const handleEditColumn = (accessor: string) => {
    const col = viewColumns.find((c: any) => c.accessor === accessor);
    if (col) {
      setEditForm({
        accessor: col.accessor || '',
        title: col.title || '',
        width: col.width || '',
        textAlignment: col.textAlignment || 'left',
        renderComponent: col.renderComponent || '',
      });
      setEditingColumn(accessor);
    }
  };

  // Save column edit (implement as needed)
  const handleSaveEdit = () => {
    setViewColumns((cols: any[]) =>
      cols.map((col: any) => {
        if (col.accessor !== editingColumn) return col;
        const updated: any = { ...col };
        if (editForm.title) updated.title = editForm.title;
        if (editForm.width) updated.width = editForm.width;
        if (editForm.textAlignment) updated.textAlignment = editForm.textAlignment;
        if (editForm.renderComponent) updated.renderComponent = editForm.renderComponent;
        return updated;
      })
    );
    setEditingColumn(null);
  };

  // Fill missing title in viewColumns from allColumns
  const filledViewColumns = sortedViewColumns.map((col: any) => {
    const fullCol = allColumns.find((c: any) => c.accessor === col.accessor);
    col.index = col.index || fullCol.index || 0;
    if (col.title) return col;
    return fullCol ? { ...col, title: fullCol.title } : col;
  });
  const filteredViewColumns = filledViewColumns.filter((col: any) => 
    col && 
    col.enabled !== false && 
    (col.title || '').toLowerCase().includes(search.toLowerCase())
  ).sort((a: any, b: any) => (a.index || 0) - (b.index || 0));

  const handleApply = async () => {
    try {
      // You may want to adjust the payload structure as needed by your API
      await editSubpage(auth0, {
        subPageKey,
        columns: viewColumns
      }).then(() => {
        onClose();
        setEditingColumn(null);
        setEditMode(false);
        window.location.reload();
      })
    } catch (e) {
      // Optionally show an error notification
      console.error('Failed to update subpage', e);
    }
  };

  const DisplayAsLabel = (
    <Tooltip label="Choose how this column should be displayed in the table" withArrow multiline>
      <Flex align="center" gap={8}>
        Display As
        <IconInfoCircle size={16} />
      </Flex>
    </Tooltip>
  )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span style={{ fontWeight: 650 }}>Edit view</span>}
      size="lg"
      radius="md"
      withCloseButton
      centered
      overlayProps={{ blur: 2 }}
      styles={{
        header: {
          marginTop: 20,
        },
        body: {
          // margin: 10,
        },
      }}
    >
      {/* <TextInput
        placeholder="Find a column"
        mb="md"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      /> */}
      <Divider mb="md" style={{ width: '100%' }} />
      <Flex gap="md" align="flex-start">
        {/* Left Panel: Current columns */}
        <Box style={{ flex: 1, minWidth: 220 }}>
          <ScrollArea h={400}>
            {filteredViewColumns.map((col: any, idx: any) => {
              if (!col) return null;
              return (
                <Group key={col.accessor} position="apart" mb={4}>
                  <Checkbox checked label={col.title} readOnly />
                  <Group spacing={0}>
                    <Tooltip label="Move up" withArrow>
                      <ActionIcon variant="subtle" onClick={() => moveColumn(col.accessor, 'up')} disabled={idx === 0}>
                        <IconChevronUp size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Move down" withArrow>
                      <ActionIcon variant="subtle" onClick={() => moveColumn(col.accessor, 'down')} disabled={idx === filteredViewColumns.length - 1}>
                        <IconChevronDown size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Edit column" withArrow>
                      <ActionIcon variant="subtle" onClick={() => handleEditColumn(col.accessor)}>
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                    {col.accessor === 'title' ? (
                      <ActionIcon variant="subtle" disabled>
                        <IconArrowBarToRight color="#5757df" size={16} />
                      </ActionIcon>
                    ) : (
                      <Tooltip label="Exclude from view" withArrow>
                        <ActionIcon
                          onClick={() => excludeColumn(col.accessor)}
                          variant="subtle"
                        >
                          <IconArrowBarToRight color="#5757df" size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Group>
              );
            })}
          </ScrollArea>
        </Box>

        <Divider orientation="vertical" />

        {/* Right: Available columns */}
        <Box style={{ flex: 1, minWidth: 240, background: '#F8F9FA' }}>
          <ScrollArea h={400}>
            {availableColumns.map((col: any) => (
              <Group
                key={col.accessor}
                position="apart"
                mb={4}
                style={{
                  width: 246,
                  height: 30,
                  gap: 10,
                  borderRadius: 4,
                  alignItems: 'center',
                  background: 'transparent',
                }}
              >
                <Group spacing={0}>
                  <Tooltip label="Include in view" withArrow>
                    <ActionIcon
                      onClick={() => includeColumn(col.accessor)}
                      variant="subtle"
                    >
                      <IconArrowBarToLeft color="#5757df" size={16} />
                    </ActionIcon>
                  </Tooltip>
                  <Text size="sm">{col.title}</Text>
                </Group>
              </Group>
            ))}
          </ScrollArea>
        </Box>
      </Flex>
      <Divider mt="md" mb="md" />
      <Flex justify="flex-end" mt="md">
        <Button color="#5757df" onClick={handleApply}>Apply</Button>
      </Flex>
      {/* Optionally, add your column edit modal here */}
      <Modal
        opened={!!editingColumn}
        onClose={() => setEditingColumn(null)}
        title="Edit column"
        size="sm"
      >
        <TextInput
          label="Title"
          value={editForm.title}
          onChange={e => setEditForm(f => ({ ...f, title: e.currentTarget.value }))}
          mb="md"
        />
        <NumberInput
          label="Width"
          value={Number(editForm.width) || ''}
          onChange={value => setEditForm(f => ({ ...f, width: value ? String(value) : '' }))}
          mb="md"
          min={0}
        />
        <Select
          label="Text Alignment"
          value={editForm.textAlignment}
          onChange={value => setEditForm(f => ({ ...f, textAlignment: value || 'left' }))}
          data={['left', 'center', 'right']}
          mb="md"
        />
        {editForm.accessor === 'status' ? (
          <TextInput
            label={
              DisplayAsLabel
            }
            value="DeliveryStatusCell"
            readOnly
            mb="md"
          />
        ) : editForm.accessor === 'tags' ? (
          <TextInput
            label={
              DisplayAsLabel
            }  
            value="TagsCell"
            readOnly
            mb="md"
          />
        ) : editForm.accessor === 'owner' ? (
          <Select
            label={
              DisplayAsLabel
            }
            value={editForm.renderComponent}
            onChange={value => setEditForm(f => ({ ...f, renderComponent: value || '' }))}
            data={['OwnerCell', 'TextCell']}
            mb="md"
          />
        ) : editForm.accessor === 'priority' ? (
          <Select
            label={
              DisplayAsLabel
            }
            value={editForm.renderComponent}
            onChange={value => setEditForm(f => ({ ...f, renderComponent: value || '' }))}
            data={['OwnerCell', 'PriorityCell']}
            mb="md"
          />
        ) : (
          <Select
            label={
              DisplayAsLabel
            }
            value={editForm.renderComponent}
            onChange={value => setEditForm(f => ({ ...f, renderComponent: value || '' }))}
            data={['TextCell', 'DefaultCell', 'BarCell']}
            mb="md"
          />
        )}
        <Button mt="md" onClick={handleSaveEdit}>
          Save
        </Button>
      </Modal>
    </Modal>
  );
};

export default EditTableViewModal;